import React from 'react';
import { moneyFormat, colors } from 'design-system';
import {
  Box,
  Grid,
  Typography,
  makeStyles,
  createStyles,
} from '@material-ui/core';
import LargeCircleCheckIcon from 'src/components/Icons/LargeCircleCheckIcon';

// Models
import { Fraction } from 'src/models/helperTypes/FractionationType';

// Functions
import {
  getDescriptionFromFractionation,
  getTitleFromFractionation,
} from 'src/functions/fractionationMethods';
import { bankTransferPayment, sinpePayment } from 'src/constants/paymentTypes';
import profiles from 'src/constants/profileTypes';

const { formatMoney } = moneyFormat;

const useStyles = makeStyles(() =>
  createStyles({
    alignCenterText: {
      textAlign: 'center',
    },
    iconStyling: {
      marginRight: 6,
      display: 'flex',
    },
    marginRightSpacing: {
      marginRight: 5,
    },
    boxContainer: {
      width: '100%',
      cursor: 'pointer',
      boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.04)',
      padding: '16px 13px',
      borderRadius: 10,
    },
    actualPrice: {
      position: 'relative',
      display: 'inline-block',
      fontSize: 16,
      fontWeight: 600,
    },
    actualPriceStrikeThrough: {
      position: 'absolute',
      top: 1,
      left: 0,
      width: '100%',
      height: '40%',
      borderBottom: '2px solid #FF7182',
      transform: 'rotate(9deg)',
      transformOrigin: 'left bottom',
    },
    discountPrice: {
      fontSize: 23,
      fontWeight: 900,
      margin: 5,
    },
    actualMonths: {
      position: 'relative',
      display: 'inline-block',
      fontSize: 14,
      fontWeight: 700,
      marginRight: 10,
    },
    actualMonthsStrikeThrough: {
      position: 'absolute',
      top: -1,
      left: 1,
      width: '130%',
      height: '30%',
      borderBottom: '2px solid #FF7182',
      transform: 'rotate(30deg)',
      transformOrigin: 'left bottom',
    },
  }),
);

const generatePriceContent = (
  classes: ReturnType<typeof useStyles>,
  fractionation: Fraction,
  isSelected: boolean,
  typeOfMoney?: string,
  profileType?: string,
) => {
  return (
    <>
      <Grid
        item
        container
        xs={12}
        direction="row"
        alignItems="center"
        justifyContent="center"
      >
        {isSelected && (
          <div className={classes.iconStyling} data-testid="check-icon-id">
            <LargeCircleCheckIcon />
          </div>
        )}
        <Typography variant="body1" data-testid="option-title">
          {getTitleFromFractionation(fractionation)}
        </Typography>
      </Grid>

      <Grid item xs={12} data-testid="price-content-id">
        {profileType === profiles.RETENCION &&
        fractionation.installmentNumber === 12 ? (
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="center"
          >
            <Typography
              variant="h4"
              className={classes.actualMonths}
              data-testid="option-description-1"
            >
              {getDescriptionFromFractionation(fractionation, 1)}
              <span className={classes.actualMonthsStrikeThrough} />
            </Typography>
            <Typography
              variant="h4"
              style={{ fontWeight: 700 }}
              data-testid="option-description-2"
            >
              {getDescriptionFromFractionation(fractionation, 2)}*
            </Typography>
          </Grid>
        ) : (
          <Typography
            variant="h4"
            style={{ fontWeight: 700 }}
            data-testid="option-description"
          >
            {getDescriptionFromFractionation(fractionation)}
          </Typography>
        )}

        {profileType === profiles.RETENCION &&
        fractionation.installmentNumber === 1 ? (
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="center"
            data-testid="price-content-id"
          >
            <Typography
              variant="h1"
              data-testid="price-id"
              className={classes.actualPrice}
            >
              {formatMoney(fractionation.installmentAmount, false, typeOfMoney)}
              <span className={classes.actualPriceStrikeThrough} />
            </Typography>
            <Typography
              variant="h2"
              data-testid="price-discount-id"
              className={classes.discountPrice}
            >
              {formatMoney(
                fractionation.installmentAmount -
                  fractionation.installmentAmount * 0.05,
                false,
                typeOfMoney,
              )}{' '}
              *
            </Typography>
          </Grid>
        ) : (
          <Typography
            variant="h2"
            data-testid="price-id"
            className={classes.discountPrice}
          >
            {formatMoney(fractionation.installmentAmount, false, typeOfMoney)}
          </Typography>
        )}

        {profileType === profiles.RETENCION && (
          <Typography
            style={{ fontWeight: 500, fontSize: '13px' }}
            data-testid="option-surcharge-description"
          >
            Beneficio incluido (1 mes GRATIS el primer año*)
          </Typography>
        )}

        {(fractionation.paymentType === sinpePayment ||
          fractionation.paymentType === bankTransferPayment) && (
          <Typography
            style={{ fontWeight: 700, fontSize: '11px' }}
            data-testid="option-surcharge-description"
          >
            {`Incluye ${fractionation.percentageApply}% de recargo`}
          </Typography>
        )}
      </Grid>
    </>
  );
};

type Props = {
  fractionation: Fraction;
  isSelected: boolean;
  typeOfMoney: string | undefined;
  onClickFraction: () => void;
  profileType?: string;
};

const PaymentOptionCard = ({
  fractionation,
  isSelected,
  typeOfMoney,
  onClickFraction,
  profileType,
}: Props): JSX.Element => {
  const classes = useStyles();

  return (
    <Box
      data-testid={`payment-option-card-${fractionation.installmentNumber}`}
      id={`payment-option-card-${fractionation.installmentNumber}`}
      onClick={onClickFraction}
      sx={{
        bgcolor: isSelected ? colors.onahau : colors.white,
      }}
      className={classes.boxContainer}
    >
      <Grid
        container
        spacing={1}
        className={classes.alignCenterText}
        data-testid="option-card-contianer"
      >
        {profileType === profiles.RETENCION &&
          generatePriceContent(
            classes,
            fractionation,
            isSelected,
            typeOfMoney,
            profileType,
          )}
        {profileType !== profiles.RETENCION &&
          generatePriceContent(
            classes,
            fractionation,
            isSelected,
            typeOfMoney,
            profileType,
          )}
      </Grid>
    </Box>
  );
};

export default PaymentOptionCard;
